import { environment } from '@/assets/environment';
import { SubmitEmailForm } from '@/models/entities/submit-email-form.model';
import { ITiresSale } from '@/models/entities/tires-sale.model';
import { IErrorResponse } from '@/models/errors/error-response.model';
import { IFilterData } from '@/models/store/filter-data/filter-data.model';
import { IFormModal } from '@/models/store/modals/form-modal.model';
import { ISnackbarState } from '@/models/store/snackbar/snackbar.model';
import { IState } from '@/models/store/state.model';
import { InjectionKey } from 'vue';
import { createStore, Store } from 'vuex';
import aboutHowBuyModule from './modules/about-how-buy.module';
import { batteriesModule } from './modules/batteries.module';
import commonEssencesModule from './modules/common-essences.module';
import contactsModule from './modules/contact.module';
import installmentModule from './modules/installment.module';
import { matsCasesModule } from './modules/mats-cases.module';
import { ringsModule } from './modules/rings.module';
import { screwModule } from './modules/screw.module';
import { wheelsModule } from './modules/wheels.module';
import { TireCount } from '@/enums/tire-count.enum';
import { IncomingEntity } from '@/models/entities/incoming-entity.model';
import { GoodType } from '@/models/entities/good-type.model';
import { AdditionalModal } from '@/models/store/modals/addditional-modal.model';
import { authModule } from './modules/auth.module';
import { clientOrdersModule } from './modules/client-orders.module';
import { tireModule } from './modules/tire.module';

export const key: InjectionKey<Store<IState>> = Symbol();

export const store = createStore<IState>({
    state: {
        isUnAuthorized: false,
        isMobile: false,
        snackbarState: { status: false, text: '', isError: false },
        support: {
            _id: '_',
            contact: 'Поддержка',
            link: '/media/icons/support_icon.svg',
            type: 'support',
        },
        formModal: { status: false, sale: false, data: {}, viewData: {}, isSupport: false },
        additionalModal: { status: false, data: {} },
        filterData: {
            tireFilter: {
                brand: [],
                width: '',
                height: '',
                season: [],
                diameter: '',
                stud: '',
                possibilityStudding: '',
            },
            cargoFilters: {},
            ringsFilter: { external: '', internal: '', rest: '', typeName: 'vector' },
            batteriesFilter: {
                typeName: '',
                brand: '',
                type: undefined,
                volume: '',
                startingCurrent: '',
            },
            wheelsFilter: {
                typeName: '',
                brand: '',
                wheelType: '',
                radius: '',
                width: '',
                pcd: '',
                et: '',
                startEt: '',
                endEt: '',
                dia: '',
            },
            screwFilter: {
                typeName: 'granatScrew',
                brand: '',
                fastenerType: '',
                length: '',
                thread: '',
                wrench: '',
            },
            caseFilter: { type: '', color: '', length: '', height: '', width: '' },
            matFilter: {
                carModel: '',
                brand: '',
                yearStart: '',
                yearEnd: '',
                type: '',
                located: '',
                matFigure: '',
            },
            additionalGoodsFilter: {
                edging: '',
                type: '',
                color: '',
                name: '',
                brand: '',
                fastenerBrand: '',
            },
        },
        paginationLength: 0,
        responseStatus: false,
        activeEntitySaleIds: [],
        tiresSale: { bagoria: '', koleso: '', shate: '' },
        currentPageNumber: 1,
        count: 10,
        tireCount: TireCount.LIST_VIEW,
        scroll: 0,
        currentViewEntity: {} as IncomingEntity,
    },
    actions: {
        changeUnAuthorized: ({ commit }, payload: boolean): void => {
            commit('setUnAuthorized', payload);
        },
        changeIsMobile: ({ commit }, payload: boolean): void => {
            commit('setIsMobile', payload);
        },
        changeSnackbar: ({ commit }, payload: ISnackbarState) => {
            commit('setSnackbarState', payload);
        },
        changeFormModal: ({ commit }, payload: IFormModal) => {
            commit('setFormModal', payload);
        },
        changeAdditionalModal: ({ commit }, payload: AdditionalModal) => {
            commit('setAdditionalModal', payload);
        },
        changePaginationLength: ({ commit }, payload: number) => {
            commit('setPaginationLength', payload);
        },
        changeFilterData: ({ commit }, payload: IFilterData): void => {
            commit('setFilterData', payload);
        },
        changeResponseStatus: ({ commit }, payload: boolean): void => {
            commit('setResponseStatus', payload);
        },
        changeActiveEntitySaleId: ({ commit }, payload: string): void => {
            commit('setActiveEntityId', payload);
        },
        changePageNumber: ({ commit }, payload: number): void => {
            commit('setPageNumber', payload);
        },
        changeTireCount: ({ commit }, payload: TireCount): void => {
            commit('setTireCount', payload);
        },
        changeScroll: ({ commit }, payload: number): void => {
            commit('setScroll', payload);
        },

        submitEmailForm: async ({ commit }, payload: SubmitEmailForm) => {
            commit('setResponseStatus', true);

            const response = await fetch(`${environment.baseUrl}/api/submit-form`, {
                method: 'post',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });
                return;
            }

            const result = (await response.json()).data;
            commit('setSnackbarState', { status: true, text: result, isError: false });
            commit('setResponseStatus', false);
            // commit('setActiveEntityId', '');
        },

        getSale: async ({ commit }) => {
            const response = await fetch(`${environment.baseUrl}/api/sale`, {
                method: 'get',
                headers: {
                    'x-app': 'secret_admin',
                    'Content-type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });
                return;
            }

            const data = (await response.json()).data.tiresSale;
            commit('setSale', data);
        },

        fetchViewEntity: async (
            { commit },
            payload: { _id: string; type: GoodType },
        ): Promise<void> => {
            commit('setResponseStatus', true);
            const params = new URLSearchParams();
            params.append('type', String(payload.type));

            const response = await fetch(
                `${environment.baseUrl}/api/view-entity/${payload._id}?` + params,
                {
                    method: 'get',
                    headers: {
                        'Content-type': 'application/json',
                    },
                },
            );

            if (!response.ok) {
                const errorObject: IErrorResponse = await response.json();
                commit('setSnackbarState', {
                    status: true,
                    text: errorObject.error,
                    isError: true,
                });
                return;
            }

            const data = (await response.json()).data;
            commit('setResponseStatus', false);
            commit('setViewEntity', data);
        },
    },

    mutations: {
        setUnAuthorized: (store, payload: boolean): void => {
            store.isUnAuthorized = payload;
        },
        setIsMobile: (store, payload: boolean): void => {
            store.isMobile = payload;
        },
        setSnackbarState: (store, payload: ISnackbarState) => {
            store.snackbarState = { ...payload };
        },
        setFormModal: (store, payload: IFormModal): void => {
            store.formModal = { ...payload };
        },
        setAdditionalModal: (store, payload: AdditionalModal): void => {
            store.additionalModal = { ...payload };
        },
        setPaginationLength: (store, payload: number): void => {
            store.paginationLength = payload;
        },
        setFilterData: (store, payload: IFilterData): void => {
            store.filterData = { ...payload };
        },
        setResponseStatus: (store, payload: boolean): void => {
            store.responseStatus = payload;
        },
        setSale: (store, payload: ITiresSale): void => {
            store.tiresSale = { ...payload };
        },
        setActiveEntityId: (store, payload: string): void => {
            store.activeEntitySaleIds.push(payload);
        },
        setPageNumber: (store, payload: number): void => {
            store.currentPageNumber = payload;
        },
        setTireCount: (store, payload: TireCount): void => {
            store.tireCount = payload;
        },
        setScroll: (store, payload: number): void => {
            store.scroll = payload;
        },
        setViewEntity: (store, payload: IncomingEntity): void => {
            store.currentViewEntity = payload;
        },
    },

    getters: {
        getIsUnAuth: (store) => store.isUnAuthorized,
        getIsMobile: (store) => store.isMobile,
        getSnackbarState: (store) => store.snackbarState,
        getFormModal: (store) => store.formModal,
        getPaginationLength: (store) => store.paginationLength,
        getFilterData: (store) => store.filterData,
        getResponseStatus: (store) => store.responseStatus,
        getTiresSale: (store) => store.tiresSale,
        getActiveEntitySaleIds: (store) => store.activeEntitySaleIds,
        getCurrentPageNumber: (store) => store.currentPageNumber,
        getCount: (store) => store.count,
        getTireCount: (store) => store.tireCount,
        getScroll: (store) => store.scroll,
        getSingleEntity: (store) => store.currentViewEntity,
        getSupport: (store) => store.support,
        getAdditionalModal: (store) => store.additionalModal,
    },

    modules: {
        contactsModule,
        tireModule,
        commonEssencesModule,
        aboutHowBuyModule,
        installmentModule,
        ringsModule,
        batteriesModule,
        wheelsModule,
        screwModule,
        matsCasesModule,
        authModule,
        clientOrdersModule,
    },
});
